//
// README - Beta Component
//
// This component is marked "beta" and will go through some iterations
// before landing at a final solution. Please reach out to the Cadence team
// if you intend to use this in the meantime and we'll help you out!
//

import React, { useState } from 'react';

import { RCComboboxBase } from '../internalComponents/RCComboboxBase/RCComboboxBase';
import { RCAutocompleteItemProps } from './RCAutocompleteItem';

interface RCAutocompleteProps {
  /** Controls state of the suggestion panel */
  isOpen: boolean;

  /** Callback to handle changes to the open state of the suggestion panel */
  onOpenChange: (isOpen: boolean) => void;

  /** Optionally set the initial value of the input */
  value?: string;

  /** Id of the input. Should also be passed to the input label / form group. */
  inputId: string;

  /** Callback function that passes the current input string. */
  onInputChange: (value: string) => void;

  /** Renders a loading indicator in the input. */
  loading: boolean;

  /** Input placeholder text. */
  placeholder?: string;

  /** Optionally disable the input */
  disabled?: boolean;

  /** An array of RCAutocompleteItem's. Do not include fragments or other components. */
  children: React.ReactElement<RCAutocompleteItemProps> | React.ReactElement<RCAutocompleteItemProps>[];
}

export function RCAutocomplete({
  isOpen,
  onOpenChange,
  placeholder = undefined,
  value = '',
  onInputChange,
  loading = false,
  disabled = false,
  inputId,
  children,
}: RCAutocompleteProps) {
  const [inputValue, setInputValue] = useState<string>(value);

  function handleInputChange(v: string) {
    setInputValue(v);
    onInputChange(v);
  }

  return (
    <div className="rc-autocomplete">
      <RCComboboxBase
        isOpen={isOpen}
        onOpenChange={(v) => onOpenChange(v)}
        type="search"
        inputValue={inputValue}
        placeholder={placeholder}
        isLoading={loading}
        disabled={disabled}
        onInputChange={(v) => handleInputChange(v)}
        inputId={inputId}
      >
        {React.Children.toArray(children).map((child) => {
          if (!React.isValidElement(child)) return null;
          const item = child as React.FunctionComponentElement<RCAutocompleteItemProps>;
          if (item.type?.displayName !== 'RCAutocompleteItem') throw new Error(
            'Children should be an array of <RCAutocompleteItem />',
          );
          return item.type?.displayName === 'RCAutocompleteItem' ? child : null;
        })}
      </RCComboboxBase>
    </div>
  );
}
